import { useNavigate, useParams } from "react-router-dom";
import {
  VentureDetail,
  useVenture,
  PledgeModal,
  ConvertToProjectButton,
  usePledgeActions,
} from "@project-mgmt/feature-ventures";
import { Container, Page, Button } from "@project-mgmt/ui";
import { useAuth } from "@project-mgmt/feature-auth";
import { routes } from "../utils/routes";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { notify } from "../providers/ToastProvider";
import { useState } from "react";
import { TabInterface, TabPanel } from "../components/TabInterface";
import { PledgesPanel } from "../components/ventures/PledgesPanel";

export function VentureDetailPage() {
  const navigate = useNavigate();
  const { workspaceId, ventureId } = useParams<{ workspaceId: string; ventureId: string }>();
  const { user } = useAuth();
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "pledges">("overview");

  // Fetch venture data
  const { venture, isLoading, error, mutate: revalidateVenture } = useVenture(ventureId ?? "", workspaceId ?? "");
  // Pledge actions
  const { createPledge, isCreating } = usePledgeActions(ventureId ?? "", workspaceId, notify);

  // Validate required params
  if (!workspaceId || !ventureId) {
    return (
      <Container>
        <Page title="Venture Detail" className="brand-page">
          <div className="p-6 text-center">
            <p className="text-red-600">Invalid workspace or venture ID</p>
            <Button onClick={() => navigate(routes.workspaces())} className="mt-4">
              Back to Workspaces
            </Button>
          </div>
        </Page>
      </Container>
    );
  }

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error state
  if (error || !venture) {
    return (
      <Container>
        <Page title="Venture Detail" className="brand-page">
          <div className="p-6 text-center">
            <p className="text-red-600">{error || "Venture not found"}</p>
            <Button onClick={() => navigate(routes.ventures(workspaceId))} className="mt-4">
              Back to Ventures
            </Button>
          </div>
        </Page>
      </Container>
    );
  }

  // Check if user is the creator
  const isCreator = user?.id === venture.createdBy;

  // Handle pledge submission
  const handlePledge = async (amount: number) => {
    const result = await createPledge(amount);
    if (result.success) {
      setShowPledgeModal(false);
      // Revalidate venture data to update pledge counts
      revalidateVenture();
    }
    return result;
  };

  // Handle opening pledge modal
  const handleOpenPledgeModal = () => {
    setShowPledgeModal(true);
  };

  return (
    <Container size="xl">
      <Page className="brand-page">
        {/* Header with Actions */}
        <div className="brand-page-hero mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="brand-page-kicker">Venture Overview</p>
            <h1 className="brand-page-title">{venture.name}</h1>
          </div>

          <div className="flex gap-3">
            {/* Edit button - only for creator and draft/live status */}
            {isCreator && (venture.status === "draft" || venture.status === "live") && (
              <Button
                variant="outline"
                onClick={() => navigate(routes.editVenture(workspaceId, ventureId))}
                className="touch-target"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit
              </Button>
            )}

            {/* Pledge button - only for live ventures and non-creators */}
            {venture.status === "live" && !isCreator && (
              <Button onClick={() => setShowPledgeModal(true)} className="touch-target">
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Pledge
              </Button>
            )}

            {/* Convert to Project button - only for successful ventures and creator */}
            {venture.status === "successful" && isCreator && (
              <ConvertToProjectButton
                venture={venture}
                currentUserId={user?.id}
                workspaceId={workspaceId}
                onSuccess={(projectId) => {
                  navigate(routes.project(workspaceId, projectId));
                }}
                notify={notify}
              />
            )}
          </div>
        </div>

        {/* Tab Interface */}
        <div className="brand-section-card mb-6 p-4 sm:p-5">
          <TabInterface
            tabs={[
              { id: "overview", label: "Overview" },
              { id: "pledges", label: "Pledges" },
            ]}
            activeTab={activeTab}
            onTabChange={(tabId) => setActiveTab(tabId as "overview" | "pledges")}
          />
        </div>

        {/* Overview Tab Panel */}
        <TabPanel id="overview" activeTab={activeTab}>
          <div className="brand-section-card p-4 sm:p-6">
            <VentureDetail venture={venture} />
          </div>
        </TabPanel>

        {/* Pledges Tab Panel */}
        <TabPanel id="pledges" activeTab={activeTab}>
          <div className="brand-section-card p-4 sm:p-6">
            <PledgesPanel
              workspaceId={workspaceId}
              ventureId={ventureId}
              ventureStatus={venture.status}
              isOwner={isCreator}
              onMakePledge={handleOpenPledgeModal}
            />
          </div>
        </TabPanel>

        {/* Pledge Modal */}
        {showPledgeModal && (
          <PledgeModal
            isOpen={showPledgeModal}
            onClose={() => setShowPledgeModal(false)}
            venture={venture}
            onPledge={handlePledge}
            isLoading={isCreating}
          />
        )}
      </Page>
    </Container>
  );
}
